<template>
  <div class="card">
    <div class="card-header">
      <h2>{{ $t('pos.title') }}</h2>
      <BaseButton
        v-if="integration && !isEditing && !readOnly"
        type="button"
        variant="secondary"

        icon="fa-pen"
        @click="startEdit"
      >
        {{ $t('pos.editIntegration') }}
      </BaseButton>
    </div>
    <div class="card-body">

      <div v-if="loading" class="loading-state">
        <AppPageLoader size="sm" direction="row">{{ $t('general.loading') }}</AppPageLoader>
      </div>

      <div v-else-if="error" class="error-state">
        <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
        <p>{{ error }}</p>
        <BaseButton variant="secondary" @click="loadIntegration">
          {{ $t('general.retry') }}
        </BaseButton>
      </div>

      <div v-else-if="!integration && !isEditing" class="empty-state">
        <font-awesome-icon icon="fa-cash-register" class="empty-icon" />
        <h3>{{ $t('pos.noIntegration') }}</h3>
        <p>{{ readOnly ? $t('navigation.viewerActionDisabledHint') : $t('pos.noIntegrationDescription') }}</p>
        <BaseButton v-if="!readOnly" type="button" icon="fa-plus" @click="startCreate">
          {{ $t('pos.addIntegration') }}
        </BaseButton>
      </div>

      <div v-else-if="isEditing && !readOnly" class="form-container">
        <form ref="posFormRef" @submit.prevent="saveIntegration">
          <div v-if="providersLoadWarning" class="providers-notice" role="status">
            <font-awesome-icon icon="fa-info-circle" />
            <span>{{ providersLoadWarning }}</span>
          </div>

          <div class="form-group">
            <label class="form-label">
              {{ $t('pos.provider') }}<span v-if="!integration" class="required-mark">*</span>
            </label>
            <select
              id="provider_code"
              v-model="form.provider_code"
              class="form-select"
              :disabled="!!integration"
              @change="onProviderChange"
            >
              <option value="" disabled>{{ $t('pos.selectProvider') }}</option>
              <option
                v-for="provider in providers"
                :key="provider.code"
                :value="provider.code"
              >
                {{ provider.name }}
              </option>
            </select>
            <p
              v-if="integration && isRemarisProvider(integration.provider_code)"
              class="form-help muted"
            >
              {{ $t('pos.remarisEditHint') }}
            </p>
            <p
              v-if="integration && isServusProvider(integration.provider_code)"
              class="form-help muted"
            >
              {{ $t('pos.servusEditHint') }}
            </p>
          </div>

          <template v-if="isRemarisForm">
            <div class="form-group remaris-credentials-block">
              <label class="form-label">{{ $t('pos.credentials') }}</label>
              <p class="form-help">{{ $t('pos.remarisCredentialsIntro') }}</p>

              <div
                v-for="field in REMARIS_FIELD_DEFS"
                :key="field.key"
                class="remaris-field"
              >
                <label class="form-label remaris-field-label">
                  <span>{{ $t(field.labelKey) }}<span v-if="field.required && !integration" class="required-mark">*</span></span>
                  <code class="json-key" :title="$t('pos.remarisJsonKeyHint')">{{ field.key }}</code>
                  <span
                    v-if="integration && field.sensitive && isCredentialConfigured(integration, field.key)"
                    class="configured-badge"
                  >
                    {{ $t('pos.credentialConfigured') }}
                  </span>
                </label>
                <p class="form-help field-help">{{ $t(field.helpKey) }}</p>
                <div class="secret-input-row">
                  <input
                    v-model="form.credentials[field.key]"
                    class="form-input"
                    :data-field="field.key"
                    :type="secretInputType(field)"
                    :placeholder="integration && field.sensitive && isCredentialConfigured(integration, field.key) ? $t('pos.credentialPlaceholder') : ''"
                    autocomplete="off"
                    spellcheck="false"
                  />
                  <template v-if="field.sensitive">
                    <BaseButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      :icon="revealedSecrets[field.key] ? 'fa-eye-slash' : 'fa-eye'"
                      :aria-label="revealedSecrets[field.key] ? $t('pos.hideCredential') : $t('pos.showCredential')"
                      :title="revealedSecrets[field.key] ? $t('pos.hideCredential') : $t('pos.showCredential')"
                      @click="toggleSecret(field.key)"
                    />
                    <BaseButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon="fa-copy"
                      :disabled="!String(form.credentials[field.key] || '').trim()"
                      :aria-label="$t('pos.copyCredential')"
                      :title="$t('pos.copyCredential')"
                      @click="copyCredential(field.key)"
                    />
                  </template>
                </div>
              </div>
            </div>
</template>

          <template v-else-if="isServusForm">
            <div class="form-group remaris-credentials-block">
              <label class="form-label">{{ $t('pos.credentials') }}</label>
              <p class="form-help">{{ $t('pos.servusCredentialsIntro') }}</p>

              <div
                v-for="field in SERVUS_FIELD_DEFS"
                :key="field.key"
                class="remaris-field"
              >
                <label class="form-label remaris-field-label">
                  <span>{{ $t(field.labelKey) }}<span v-if="field.required && !integration" class="required-mark">*</span></span>
                  <code class="json-key" :title="$t('pos.remarisJsonKeyHint')">{{ field.key }}</code>
                  <span
                    v-if="integration && field.sensitive && isCredentialConfigured(integration, field.key)"
                    class="configured-badge"
                  >
                    {{ $t('pos.credentialConfigured') }}
                  </span>
                </label>
                <p class="form-help field-help">{{ $t(field.helpKey) }}</p>
                <div class="secret-input-row">
                  <input
                    v-model="form.credentials[field.key]"
                    class="form-input"
                    :data-field="field.key"
                    :type="secretInputType(field)"
                    :placeholder="integration && field.sensitive && isCredentialConfigured(integration, field.key) ? $t('pos.credentialPlaceholder') : ''"
                    autocomplete="off"
                    spellcheck="false"
                  />
                  <template v-if="field.sensitive">
                    <BaseButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      :icon="revealedSecrets[field.key] ? 'fa-eye-slash' : 'fa-eye'"
                      :aria-label="revealedSecrets[field.key] ? $t('pos.hideCredential') : $t('pos.showCredential')"
                      :title="revealedSecrets[field.key] ? $t('pos.hideCredential') : $t('pos.showCredential')"
                      @click="toggleSecret(field.key)"
                    />
                    <BaseButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon="fa-copy"
                      :disabled="!String(form.credentials[field.key] || '').trim()"
                      :aria-label="$t('pos.copyCredential')"
                      :title="$t('pos.copyCredential')"
                      @click="copyCredential(field.key)"
                    />
                  </template>
                </div>
              </div>
            </div>
</template>

          <div v-else-if="form.provider_code" class="form-group">
            <label class="form-label">{{ $t('pos.credentials') }}</label>
            <p class="form-help">{{ $t('pos.credentialsGenericDescription') }}</p>
            <div class="credentials-list">
              <div
                v-for="(value, key) in form.credentials"
                :key="String(key)"
                class="credential-row"
              >
                <input
                  type="text"
                  :value="key"
                  @input="updateCredentialKey(String(key), ($event.target as HTMLInputElement).value)"
                  class="form-input credential-key"
                  :placeholder="$t('pos.credentialKeyPlaceholder')"
                />
                <input
                  type="text"
                  v-model="form.credentials[String(key)]"
                  class="form-input credential-value"
                  :placeholder="$t('pos.credentialValuePlaceholder')"
                />
                <BaseButton
                  type="button"
                  variant="danger"
                  icon="fa-times"
                  @click="removeCredential(String(key))"
                />
              </div>
            </div>
            <BaseButton
              type="button"
              variant="secondary"
              icon="fa-plus"
              class="mt-2"
              @click="addCredential"
            >
              {{ $t('pos.addCredential') }}
            </BaseButton>
          </div>

          <div class="form-group">
            <label class="form-checkbox">
              <input type="checkbox" v-model="form.is_active" />
              <span>{{ $t('pos.isActive') }}</span>
            </label>
          </div>

          <div class="form-group">
            <label class="form-checkbox">
              <input type="checkbox" v-model="form.ordering_enabled" />
              <span>{{ $t('pos.orderingEnabled') }}</span>
            </label>
          </div>

          <div class="form-actions">
            <BaseButton
              v-if="integration"
              type="button"
              variant="danger"
              icon="fa-trash"
              @click="confirmDelete"
            >
              {{ $t('pos.deleteIntegration') }}
            </BaseButton>
          </div>

          <div v-if="syncMessage" class="sync-message form-sync-message" :class="{ error: syncError }">
            <font-awesome-icon :icon="syncError ? 'fa-times-circle' : 'fa-check-circle'" />
            <span>{{ syncMessage }}</span>
          </div>
        </form>
      </div>

      
      <div v-else-if="integration" class="integration-status">
        <div v-if="oneTimeApiKey && integration && isRemarisProvider(integration.provider_code)" class="one-time-key-banner">
          <p><strong>{{ $t('pos.oneTimeApiKeyWarning') }}</strong></p>
          <code class="one-time-key-value">{{ oneTimeApiKey }}</code>
          <BaseButton type="button" variant="secondary" class="mt-2" @click="dismissOneTimeKey">
            {{ $t('pos.dismissOneTimeKey') }}
          </BaseButton>
        </div>

        <div class="status-grid">
          <div class="status-item">
            <span class="status-label">{{ $t('pos.provider') }}</span>
            <span class="status-value">{{ integration.provider_name || integration.provider_code }}</span>
          </div>
          <div v-if="integration.order_hub_base_url && isRemarisProvider(integration.provider_code)" class="status-item">
            <span class="status-label">{{ $t('pos.orderHubBaseUrl') }}</span>
            <span class="status-value wrap">{{ integration.order_hub_base_url }}</span>
          </div>
          <div
            v-if="getStoredCredentialValue(integration, 'api_base_url') && isRemarisProvider(integration.provider_code)"
            class="status-item"
          >
            <span class="status-label">{{ $t('pos.remarisApiBaseUrl') }}</span>
            <span class="status-value wrap">{{ getStoredCredentialValue(integration, 'api_base_url') }}</span>
          </div>
          <div
            v-if="getStoredCredentialValue(integration, 'register_id') && isRemarisProvider(integration.provider_code)"
            class="status-item"
          >
            <span class="status-label">{{ $t('pos.remarisRegisterId') }}</span>
            <span class="status-value">{{ getStoredCredentialValue(integration, 'register_id') }}</span>
          </div>
          <div
            v-if="getStoredCredentialValue(integration, 'system_name') && isRemarisProvider(integration.provider_code)"
            class="status-item"
          >
            <span class="status-label">{{ $t('pos.remarisSystemName') }}</span>
            <span class="status-value">{{ getStoredCredentialValue(integration, 'system_name') }}</span>
          </div>
          <div
            v-if="isRemarisProvider(integration.provider_code) && isCredentialConfigured(integration, 'gm_api_key')"
            class="status-item"
          >
            <span class="status-label">{{ $t('pos.remarisGmApiKey') }}</span>
            <span class="status-value configured-text">{{ $t('pos.credentialConfigured') }}</span>
          </div>
          <div v-if="integration.pos_store_id && isRemarisProvider(integration.provider_code)" class="status-item">
            <span class="status-label">{{ $t('pos.posStoreId') }}</span>
            <span class="status-value">{{ integration.pos_store_id }}</span>
          </div>
          <div class="status-item">
            <span class="status-label">{{ $t('pos.status') }}</span>
            <span class="status-badge" :class="{ active: integration.is_active }">
              {{ integration.is_active ? $t('pos.statusActive') : $t('pos.statusInactive') }}
            </span>
          </div>
          <div class="status-item">
            <span class="status-label">{{ $t('pos.lastSync') }}</span>
            <span class="status-value">
              {{ integration.last_sync_at ? formatDate(integration.last_sync_at) : $t('pos.lastSyncNever') }}
            </span>
          </div>
        </div>

        
        <div v-if="integration.last_sync_err" class="sync-error">
          <font-awesome-icon icon="fa-exclamation-triangle" />
          <span>{{ $t('pos.lastSyncError') }}: {{ formatPosError(integration.last_sync_err) }}</span>
        </div>

        
        <div v-if="!readOnly && (isRemarisProvider(integration.provider_code) || isServusProvider(integration.provider_code))" class="sync-actions">
          <BaseButton
            type="button"
            variant="secondary"
            :loading="testingConnection"
            icon="fa-plug"
            @click="testConnection"
          >
            {{ isServusProvider(integration.provider_code) ? $t('pos.testServusConnection') : $t('pos.testConnection') }}
          </BaseButton>
          <template v-if="isRemarisProvider(integration.provider_code)">
            <BaseButton
              type="button"
              variant="secondary"
              icon="fa-key"
              @click="showRegenerateKeyConfirm = true"
            >
              {{ $t('pos.regenerateExternalApiKey') }}
            </BaseButton>
            <p class="sync-help">{{ $t('pos.regenerateExternalApiKeyHelp') }}</p>
</template>
          <p v-if="isServusProvider(integration.provider_code)" class="sync-help">
            {{ $t('pos.servusPushModelHelp') }}
          </p>
        </div>

        
        <div v-if="!readOnly" class="sync-actions">
          <BaseButton
            type="button"
            :loading="syncing"
            :disabled="!integration.is_active"
            :icon="syncing ? undefined : 'fa-sync'"
            @click="syncMenu"
          >
            {{ syncing ? $t('pos.syncInProgress') : $t('pos.syncMenu') }}
          </BaseButton>
          <p class="sync-help">{{ $t('pos.syncMenuDescription') }}</p>
        </div>

        
        <div v-if="syncMessage" class="sync-message" :class="{ error: syncError }">
          <font-awesome-icon :icon="syncError ? 'fa-times-circle' : 'fa-check-circle'" />
          <span>{{ syncMessage }}</span>
        </div>
      </div>
    </div>
  </div>

  
  <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
    <div class="modal-content">
      <h3>{{ $t('pos.confirmDelete') }}</h3>
      <p>{{ $t('pos.confirmDeleteWarning') }}</p>
      <div class="modal-actions">
        <BaseButton variant="secondary" @click="showDeleteConfirm = false">
          {{ $t('general.cancel') }}
        </BaseButton>
        <BaseButton variant="danger" :loading="deleting" @click="deleteIntegration">
          {{ $t('general.delete') }}
        </BaseButton>
      </div>
    </div>
  </div>

  <div v-if="showRegenerateKeyConfirm" class="modal-overlay" @click.self="showRegenerateKeyConfirm = false">
    <div class="modal-content">
      <h3>{{ $t('pos.confirmRegenerateApiKeyTitle') }}</h3>
      <p>{{ $t('pos.confirmRegenerateApiKeyBody') }}</p>
      <div class="modal-actions">
        <BaseButton variant="secondary" @click="showRegenerateKeyConfirm = false">
          {{ $t('general.cancel') }}
        </BaseButton>
        <BaseButton variant="danger" :loading="regeneratingKey" icon="fa-key" @click="doRegenerateExternalApiKey">
          {{ $t('pos.regenerateExternalApiKey') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseButton from '@/components/shared/BaseButton.vue';
import { posApi, type POSIntegration, type POSCredentials } from '@/services/api';
import {
  FALLBACK_POS_PROVIDERS,
  REMARIS_CREDENTIAL_KEYS,
  REMARIS_FIELD_DEFS,
  SERVUS_FIELD_DEFS,
  buildCredentialsPayloadForUpdate,
  emptyCredentialsForProvider,
  getProviderFieldDefs,
  getStoredCredentialValue,
  isCredentialConfigured,
  isRemarisProvider,
  isServusProvider,
  normalizeProviderCode,
  normalizeRemarisApiBaseUrl,
  providerCredentialsChanged,
  resolveCredentialsForForm,
  resolvePosErrorMessage,
} from '@/constants/posIntegration';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { useNotificationStore } from '@/stores/notification';
import { useFormFloatingActions } from '@/composables/useFormFloatingActions';
import { scrollToFirstEmptyCredential, scrollToFirstFormError, stopGuidedFormErrorNavigation } from '@/utils/scrollToFirstFormError';

const posFormRef = ref<HTMLFormElement | null>(null);

const props = defineProps<{
  locationId: string;

  readOnly?: boolean;
}>();

const { t, locale } = useI18n();
const notificationStore = useNotificationStore();

const loading = ref(true);
const error = ref<string | null>(null);
const saving = ref(false);
const syncing = ref(false);
const deleting = ref(false);

const integration = ref<POSIntegration | null>(null);
const providers = ref([...FALLBACK_POS_PROVIDERS]);
const providersLoadWarning = ref<string | null>(null);
const isEditing = ref(false);
const showDeleteConfirm = ref(false);
const showRegenerateKeyConfirm = ref(false);

const syncMessage = ref<string | null>(null);
const syncError = ref(false);
const oneTimeApiKey = ref<string | null>(null);
const testingConnection = ref(false);
const regeneratingKey = ref(false);
const revealedSecrets = ref<Record<string, boolean>>({});

const form = reactive({
  provider_code: '',
  credentials: {} as POSCredentials,
  is_active: true,
  ordering_enabled: true,
});

const formProviderCode = computed(() => {
  if (isEditing.value && integration.value?.provider_code) {
    return normalizeProviderCode(integration.value.provider_code);
  }
  return normalizeProviderCode(form.provider_code);
});
const isRemarisForm = computed(() => isRemarisProvider(formProviderCode.value));
const isServusForm = computed(() => isServusProvider(formProviderCode.value));

const loadProviders = async () => {
  providersLoadWarning.value = null;
  try {
    const list = await posApi.getProviders();
    if (list.length > 0) {
      providers.value = list;
    } else {
      providers.value = [...FALLBACK_POS_PROVIDERS];
      providersLoadWarning.value = t('pos.providersLoadWarning');
    }
  } catch {
    providers.value = [...FALLBACK_POS_PROVIDERS];
    providersLoadWarning.value = t('pos.providersLoadWarning');
  }
};

const loadIntegration = async () => {
  if (!props.locationId) {
    integration.value = null;
    loading.value = false;
    error.value = null;
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const data = await posApi.getIntegration(props.locationId);
    integration.value = data?.uuid ? data : null;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      integration.value = null;
      error.value = null;
    } else {
      error.value = getApiErrorMessage(err, t('pos.errorLoading'));
    }
  } finally {
    loading.value = false;
  }
};

const resetCredentialsForProvider = (code: string) => {
  setFormCredentials(emptyCredentialsForProvider(code));
};

function secretInputType(field: { key: string; sensitive?: boolean }): 'text' | 'password' {
  if (!field.sensitive || revealedSecrets.value[field.key]) return 'text';
  return 'password';
}

function toggleSecret(key: string) {
  revealedSecrets.value = { ...revealedSecrets.value, [key]: !revealedSecrets.value[key] };
}

async function copyCredential(key: string) {
  const value = String(form.credentials[key] ?? '').trim();
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    notificationStore.showNotification({ message: t('pos.credentialCopied'), type: 'success' });
  } catch {
    notificationStore.showNotification({ message: t('pos.credentialCopyFailed'), type: 'error' });
  }
}

watch(isEditing, (editing) => {
  if (!editing) revealedSecrets.value = {};
});

const setFormCredentials = (credentials: POSCredentials) => {
  for (const key of Object.keys(form.credentials)) {
    delete form.credentials[key];
  }
  Object.assign(form.credentials, credentials);
};

const populateEditForm = () => {
  const current = integration.value;
  if (!current) return;
  const code = normalizeProviderCode(current.provider_code);
  form.provider_code = code;
  setFormCredentials(resolveCredentialsForForm(code, current));
  form.is_active = current.is_active;
  form.ordering_enabled = current.ordering_enabled !== false;
};

const onProviderChange = () => {
  if (integration.value) return;
  resetCredentialsForProvider(form.provider_code);
};

const startCreate = () => {
  const code = providers.value.length > 0 ? providers.value[0].code : '';
  form.provider_code = code;
  resetCredentialsForProvider(code);
  form.is_active = true;
  form.ordering_enabled = true;
  oneTimeApiKey.value = null;
  isEditing.value = true;
};

const startEdit = async () => {
  if (!integration.value) return;
  syncMessage.value = null;
  syncError.value = false;
  try {
    const fresh = await posApi.getIntegration(props.locationId);
    if (fresh) {
      integration.value = fresh;
    }
  } catch (err: any) {
    syncMessage.value = getApiErrorMessage(err, t('pos.errorLoading'));
    syncError.value = true;
    return;
  }
  populateEditForm();
  isEditing.value = true;
};

const cancelEdit = () => {
  isEditing.value = false;
  providersLoadWarning.value = null;
  syncMessage.value = null;
  syncError.value = false;
};

const addCredential = () => {
  const newKey = `field_${Object.keys(form.credentials).length + 1}`;
  form.credentials[newKey] = '';
};

const removeCredential = (key: string) => {
  delete form.credentials[key];
};

const updateCredentialKey = (oldKey: string, newKey: string) => {
  if (oldKey === newKey || !newKey.trim()) return;
  const value = form.credentials[oldKey];
  delete form.credentials[oldKey];
  form.credentials[newKey.trim()] = value;
};

function buildProviderCredentialsPayload(code: string): POSCredentials | null {
  const defs = getProviderFieldDefs(code);
  if (!defs) return null;
  const out: POSCredentials = {};
  for (const f of defs) {
    let value = String(form.credentials[f.key] ?? '').trim();
    if (f.key === REMARIS_CREDENTIAL_KEYS.apiBaseUrl && value) {
      value = normalizeRemarisApiBaseUrl(value);
    }
    out[f.key] = value;
  }
  for (const f of defs) {
    if (f.required && out[f.key] === '') return null;
  }
  return out;
}

function providerCredentialsDirty(code: string): boolean {
  if (!integration.value) {
    return providerCredentialsChanged(code, { credentials: {}, credentials_preview: undefined }, form.credentials);
  }
  return providerCredentialsChanged(code, integration.value, form.credentials);
}

async function scrollToPosCredentialError(code: string): Promise<void> {
  const defs = getProviderFieldDefs(code);
  if (defs) {
    await scrollToFirstEmptyCredential(defs, form.credentials, posFormRef.value);
  }
}

function validateIntegrationForm(): boolean {
  if (!integration.value && !form.provider_code) return false;
  return true;
}

const saveIntegration = async () => {
  saving.value = true;
  syncMessage.value = null;
  try {
    if (integration.value) {
      let credentials: POSCredentials | undefined;
      const fieldDefs = getProviderFieldDefs(form.provider_code);
      if (fieldDefs) {
        if (!providerCredentialsDirty(form.provider_code)) {
          credentials = undefined;
        } else {
          const creds = integration.value
            ? buildCredentialsPayloadForUpdate(form.provider_code, form.credentials, integration.value.credentials)
            : buildProviderCredentialsPayload(form.provider_code);
          if (!creds) {
            syncMessage.value = isServusProvider(form.provider_code)
              ? t('pos.servusFillAllRequired')
              : t('pos.remarisFillAllRequired');
            syncError.value = true;
            saving.value = false;
            await scrollToPosCredentialError(form.provider_code);
            return;
          }
          credentials = creds;
        }
      } else {
        const hasCredentials = Object.keys(form.credentials).length > 0;
        credentials = hasCredentials ? form.credentials : undefined;
      }
      await posApi.updateIntegration(props.locationId, {
        credentials,
        is_active: form.is_active,
        ordering_enabled: form.ordering_enabled,
      });
      syncMessage.value = t('pos.integrationUpdated');
    } else {
      let payloadCreds: POSCredentials;
      const fieldDefs = getProviderFieldDefs(form.provider_code);
      if (fieldDefs) {
        const creds = buildProviderCredentialsPayload(form.provider_code);
        if (!creds) {
          syncMessage.value = isServusProvider(form.provider_code)
            ? t('pos.servusFillAllRequired')
            : t('pos.remarisFillAllRequired');
          syncError.value = true;
          saving.value = false;
          await scrollToPosCredentialError(form.provider_code);
          return;
        }
        payloadCreds = creds;
      } else {
        if (!form.provider_code) {
          syncMessage.value = t('pos.selectProvider');
          syncError.value = true;
          saving.value = false;
          await scrollToFirstFormError(
            { provider_code: syncMessage.value },
            {
              root: posFormRef.value,
              fieldOrder: ['provider_code'],
              validate: () => Boolean(form.provider_code),
            },
          );
          return;
        }
        payloadCreds = form.credentials;
      }
      const created = await posApi.createIntegration(props.locationId, {
        provider_code: form.provider_code,
        credentials: payloadCreds,
      });
      oneTimeApiKey.value = created.external_orders_api_key ?? null;
      syncMessage.value = t('pos.integrationCreated');
    }
    syncError.value = false;
    isEditing.value = false;
    providersLoadWarning.value = null;
    stopGuidedFormErrorNavigation();
    await loadIntegration();
  } catch (err: any) {
    syncMessage.value = formatPosError(err.response?.data) || t('pos.errorCreating');
    syncError.value = true;
  } finally {
    saving.value = false;
  }
};

useFormFloatingActions({
  validate: validateIntegrationForm,
  submit: saveIntegration,
  cancel: cancelEdit,
  getSubmitLabel: () => t('pos.saveIntegration'),
  getLoading: () => saving.value,
  active: () => isEditing.value && !props.readOnly,
});

const dismissOneTimeKey = () => {
  oneTimeApiKey.value = null;
};

const testConnection = async () => {
  testingConnection.value = true;
  syncMessage.value = null;
  try {
    const result = await posApi.testRemarisConnection(props.locationId);
    if (result?.ok === true) {
      syncMessage.value = result.message || t('pos.testConnectionOk');
      syncError.value = false;
    } else {
      syncMessage.value = formatPosError(result) || t('pos.testConnectionFail');
      syncError.value = true;
    }
  } catch (err: any) {
    syncMessage.value = formatPosError(err.response?.data) || t('pos.testConnectionFail');
    syncError.value = true;
  } finally {
    testingConnection.value = false;
  }
};

const doRegenerateExternalApiKey = async () => {
  regeneratingKey.value = true;
  syncMessage.value = null;
  try {
    const data = await posApi.regenerateExternalApiKey(props.locationId);
    oneTimeApiKey.value = data.external_orders_api_key ?? null;
    showRegenerateKeyConfirm.value = false;
    syncMessage.value = t('pos.regenerateApiKeySuccess');
    syncError.value = false;
    await loadIntegration();
  } catch (err: any) {
    syncMessage.value = formatPosError(err.response?.data) || t('pos.regenerateApiKeyFail');
    syncError.value = true;
  } finally {
    regeneratingKey.value = false;
  }
};

const confirmDelete = () => {
  showDeleteConfirm.value = true;
};

const deleteIntegration = async () => {
  deleting.value = true;
  try {
    await posApi.deleteIntegration(props.locationId);
    integration.value = null;
    isEditing.value = false;
    showDeleteConfirm.value = false;
    syncMessage.value = t('pos.integrationDeleted');
    syncError.value = false;
  } catch (err: any) {
    syncMessage.value = formatPosError(err.response?.data) || t('pos.errorDeleting');
    syncError.value = true;
  } finally {
    deleting.value = false;
  }
};

const syncMenu = async () => {
  syncing.value = true;
  syncMessage.value = null;
  try {
    const result = await posApi.syncMenu(props.locationId);
    syncMessage.value = `${t('pos.syncSuccess')} (${result.count} artikala)`;
    syncError.value = false;
    await loadIntegration();
  } catch (err: any) {
    const errorData = err.response?.data;
    syncMessage.value = formatPosError(errorData) || t('pos.syncFailed');
    syncError.value = true;
    await loadIntegration();
  } finally {
    syncing.value = false;
  }
};

const formatPosError = (payload: Parameters<typeof resolvePosErrorMessage>[0]) =>
  resolvePosErrorMessage(payload, t);

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

watch(
  () => props.locationId,
  async (locationId) => {
    if (!locationId) {
      integration.value = null;
      loading.value = false;
      return;
    }
    isEditing.value = false;
    syncMessage.value = null;
    await Promise.all([loadProviders(), loadIntegration()]);
  },
  { immediate: true },
);
</script>

<style scoped>
.card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.card-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.card-body {
  padding: 1.5rem;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  color: #6b7280;
}

.empty-state {
  text-align: center;
  padding: 2rem;
}

.empty-icon {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
}

.empty-state p {
  margin: 0 0 1.5rem;
  color: #6b7280;
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #dc2626;
}

.error-state .error-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.form-container {
  max-width: 640px;
}

.providers-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1e40af;
}

.remaris-credentials-block {
  padding: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.remaris-field {
  margin-bottom: 1.25rem;
}

.remaris-field:last-child {
  margin-bottom: 0;
}

.remaris-field-label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

code.json-key {
  font-size: 0.75rem;
  font-family: ui-monospace, monospace;
  padding: 0.15rem 0.4rem;
  background: #e5e7eb;
  border-radius: 0.25rem;
  color: #374151;
}

.configured-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  background: #dcfce7;
  color: #15803d;
}

.configured-text {
  color: #15803d;
  font-weight: 500;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-help {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.75rem;
}

.form-help.muted {
  color: #9ca3af;
}

.form-help.field-help {
  margin: 0 0 0.35rem;
}

.form-select,
.secret-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.secret-input-row .form-input {
  flex: 1;
  min-width: 0;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-select:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.credentials-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.credential-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.credential-key {
  flex: 1;
}

.credential-value {
  flex: 2;
}

.form-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.form-checkbox input {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.form-sync-message {
  margin-top: 1rem;
}

.integration-status {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.status-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.status-value {
  font-weight: 500;
  color: #111827;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #fef2f2;
  color: #b91c1c;
  width: fit-content;
}

.status-badge.active {
  background-color: #dcfce7;
  color: #15803d;
}

.sync-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1rem;
  background-color: #fef2f2;
  border-radius: 0.5rem;
  color: #b91c1c;
  font-size: 0.875rem;
}

.sync-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sync-help {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.sync-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background-color: #dcfce7;
  border-radius: 0.5rem;
  color: #15803d;
  font-size: 0.875rem;
}

.sync-message.error {
  background-color: #fef2f2;
  color: #b91c1c;
}

.one-time-key-banner {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.one-time-key-value {
  display: block;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #fff;
  border-radius: 0.25rem;
  word-break: break-all;
  font-size: 0.8rem;
}

.status-value.wrap {
  word-break: break-all;
}

.mt-2 {
  margin-top: 0.5rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  max-width: 480px;
  width: 90%;
}

.modal-content h3 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-content p {
  margin: 0 0 1.5rem;
  color: #6b7280;
}

.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
}

.required-mark {
  color: #dc2626;
  margin-left: 0.15rem;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .form-actions :deep(.base-btn) {
    width: 100%;
    justify-content: center;
  }

  .credential-row {
    flex-direction: column;
  }

  .credential-key,
  .credential-value {
    width: 100%;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }
}
</style>
